import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';

interface Product {
  id: string;
  title: string;
  price: number;
  image: string;
  category: string;
  rating: number;
  reviews: number;
  stock: number;
}

const SearchPage = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [sortBy, setSortBy] = useState<string>('featured');
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    { name: 'all', label: 'Tümü', icon: 'ri-apps-line' },
    { name: 'Electronics', label: 'Elektronik', icon: 'ri-smartphone-line' },
    { name: 'Fashion', label: 'Moda', icon: 'ri-shirt-line' },
    { name: 'Home & Living', label: 'Ev & Yaşam', icon: 'ri-home-4-line' },
    { name: 'Beauty', label: 'Güzellik', icon: 'ri-heart-pulse-line' },
    { name: 'Sports', label: 'Spor', icon: 'ri-basketball-line' },
    { name: 'Books', label: 'Kitap', icon: 'ri-book-open-line' }
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, searchQuery, selectedCategory, priceRange, sortBy]);

  const loadProducts = () => {
    // Mock products
    const mockProducts: Product[] = [
      {
        id: '1',
        title: 'Premium Wireless Headphones',
        price: 299.99,
        image: 'https://public.readdy.ai/ai/img_res/c60fc724c83a07829e305a57f80fdb15.jpg',
        category: 'Electronics',
        rating: 4.8,
        reviews: 234,
        stock: 45
      },
      {
        id: '2',
        title: 'Minimalist Watch Collection',
        price: 189.99,
        image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20wristwatch%20with%20clean%20dial%20and%20leather%20strap%20on%20pure%20white%20background%20with%20professional%20product%20lighting%20highlighting%20the%20refined%20details%20and%20premium%20craftsmanship%20of%20luxury%20timepiece&width=400&height=400&seq=search-prod-2&orientation=squarish',
        category: 'Fashion',
        rating: 4.9,
        reviews: 456,
        stock: 23
      },
      {
        id: '3',
        title: 'Smart Fitness Tracker',
        price: 149.99,
        image: 'https://readdy.ai/api/search-image?query=Modern%20fitness%20tracker%20smartwatch%20with%20vibrant%20display%20on%20clean%20white%20background%20with%20studio%20lighting%20showcasing%20sleek%20design%20and%20advanced%20technology%20features%20perfect%20for%20active%20lifestyle%20product%20photography&width=400&height=400&seq=search-prod-3&orientation=squarish',
        category: 'Electronics',
        rating: 4.7,
        reviews: 189,
        stock: 67
      },
      {
        id: '4',
        title: 'Designer Sunglasses',
        price: 129.99,
        image: 'https://readdy.ai/api/search-image?query=Stylish%20designer%20sunglasses%20with%20modern%20frame%20on%20pure%20white%20background%20with%20professional%20studio%20lighting%20highlighting%20premium%20materials%20and%20contemporary%20fashion%20design%20perfect%20for%20luxury%20product%20photography&width=400&height=400&seq=search-prod-4&orientation=squarish',
        category: 'Fashion',
        rating: 4.6,
        reviews: 312,
        stock: 89
      },
      {
        id: '5',
        title: 'Portable Bluetooth Speaker',
        price: 79.99,
        image: 'https://readdy.ai/api/search-image?query=Compact%20portable%20bluetooth%20speaker%20with%20modern%20design%20on%20clean%20white%20background%20with%20studio%20lighting%20showcasing%20premium%20build%20quality%20and%20sleek%20aesthetic%20perfect%20for%20tech%20product%20photography&width=400&height=400&seq=search-prod-5&orientation=squarish',
        category: 'Electronics',
        rating: 4.5,
        reviews: 567,
        stock: 120
      },
      {
        id: '6',
        title: 'Leather Crossbody Bag',
        price: 159.99,
        image: 'https://readdy.ai/api/search-image?query=Premium%20leather%20crossbody%20bag%20with%20elegant%20design%20on%20pure%20white%20background%20with%20professional%20studio%20lighting%20highlighting%20fine%20craftsmanship%20and%20luxurious%20texture%20perfect%20for%20fashion%20product%20photography&width=400&height=400&seq=search-prod-6&orientation=squarish',
        category: 'Fashion',
        rating: 4.8,
        reviews: 423,
        stock: 34
      },
      {
        id: '7',
        title: 'Yoga Mat Premium',
        price: 49.99,
        image: 'https://readdy.ai/api/search-image?query=High%20quality%20yoga%20mat%20rolled%20up%20in%20elegant%20color%20on%20clean%20white%20background%20with%20studio%20lighting%20showcasing%20premium%20texture%20and%20non%20slip%20surface%20perfect%20for%20fitness%20product%20photography&width=400&height=400&seq=search-prod-7&orientation=squarish',
        category: 'Sports',
        rating: 4.7,
        reviews: 289,
        stock: 156
      },
      {
        id: '8',
        title: 'Ceramic Coffee Mug Set',
        price: 39.99,
        image: 'https://readdy.ai/api/search-image?query=Beautiful%20ceramic%20coffee%20mug%20set%20with%20minimalist%20design%20on%20clean%20white%20background%20with%20soft%20studio%20lighting%20showcasing%20elegant%20shapes%20and%20premium%20quality%20perfect%20for%20home%20product%20photography&width=400&height=400&seq=search-prod-8&orientation=squarish',
        category: 'Home & Living',
        rating: 4.6,
        reviews: 178,
        stock: 92
      },
      {
        id: '9',
        title: 'Organic Skincare Set',
        price: 89.99,
        image: 'https://readdy.ai/api/search-image?query=Luxury%20organic%20skincare%20product%20set%20with%20elegant%20packaging%20on%20clean%20white%20background%20with%20soft%20studio%20lighting%20showcasing%20premium%20natural%20beauty%20products%20perfect%20for%20cosmetics%20photography&width=400&height=400&seq=search-prod-9&orientation=squarish',
        category: 'Beauty',
        rating: 4.9,
        reviews: 512,
        stock: 78
      },
      {
        id: '10',
        title: 'Bestseller Book Collection',
        price: 34.99,
        image: 'https://readdy.ai/api/search-image?query=Stack%20of%20bestseller%20books%20with%20beautiful%20covers%20on%20clean%20white%20background%20with%20soft%20studio%20lighting%20showcasing%20elegant%20design%20and%20premium%20quality%20perfect%20for%20book%20product%20photography&width=400&height=400&seq=search-prod-10&orientation=squarish',
        category: 'Books',
        rating: 4.8,
        reviews: 645,
        stock: 203
      },
      {
        id: '11',
        title: 'Wireless Gaming Mouse',
        price: 69.99,
        image: 'https://readdy.ai/api/search-image?query=Modern%20wireless%20gaming%20mouse%20with%20RGB%20lighting%20on%20clean%20white%20background%20with%20studio%20lighting%20showcasing%20ergonomic%20design%20and%20premium%20build%20quality%20perfect%20for%20tech%20product%20photography&width=400&height=400&seq=search-prod-11&orientation=squarish',
        category: 'Electronics',
        rating: 4.7,
        reviews: 398,
        stock: 145
      },
      {
        id: '12',
        title: 'Cotton T-Shirt Pack',
        price: 44.99,
        image: 'https://readdy.ai/api/search-image?query=Premium%20cotton%20t-shirt%20pack%20folded%20neatly%20on%20clean%20white%20background%20with%20studio%20lighting%20showcasing%20soft%20fabric%20and%20quality%20stitching%20perfect%20for%20fashion%20product%20photography&width=400&height=400&seq=search-prod-12&orientation=squarish',
        category: 'Fashion',
        rating: 4.5,
        reviews: 267,
        stock: 189
      }
    ];

    setProducts(mockProducts);
    setLoading(false);
  };

  const filterAndSortProducts = () => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Price range filter
    filtered = filtered.filter(
      product => product.price >= priceRange[0] && product.price <= priceRange[1]
    );

    // Sort
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => b.rating - a.rating);
        break;
      case 'popular':
        filtered.sort((a, b) => b.reviews - a.reviews);
        break;
      default:
        // featured - keep original order
        break;
    }

    setFilteredProducts(filtered);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setPriceRange([0, 1000]);
    setSortBy('featured');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-display font-bold text-gray-900 mb-4">Ürün Ara</h1>
            <p className="text-gray-600">Aradığınız ürünü bulun</p>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative max-w-2xl">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Ürün ara..."
                className="w-full px-6 py-4 pl-14 text-sm border-2 border-gray-200 rounded-2xl focus:border-pink-500 focus:outline-none shadow-lg"
              />
              <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-xl text-gray-400"></i>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-24">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-gray-900">Filtreler</h2>
                  <button
                    onClick={resetFilters}
                    className="text-sm text-pink-600 hover:text-pink-700 font-medium cursor-pointer"
                  >
                    Sıfırla
                  </button>
                </div>

                {/* Categories */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Kategori</h3>
                  <div className="space-y-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.name}
                        onClick={() => setSelectedCategory(cat.name)}
                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                          selectedCategory === cat.name
                            ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <i className={`${cat.icon} text-lg`}></i>
                        <span>{cat.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Fiyat Aralığı: ${priceRange[0]} - ${priceRange[1]}
                  </h3>
                  <div className="space-y-3">
                    <input
                      type="range"
                      min="0"
                      max="1000"
                      value={priceRange[0]}
                      onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                      className="w-full"
                    />
                    <input
                      type="range"
                      min="0"
                      max="1000"
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Sort */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Sıralama</h3>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-4 py-2 text-sm border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:outline-none cursor-pointer"
                  >
                    <option value="featured">Öne Çıkanlar</option>
                    <option value="price-low">Fiyat: Düşükten Yükseğe</option>
                    <option value="price-high">Fiyat: Yüksekten Düşüğe</option>
                    <option value="rating">En Yüksek Puan</option>
                    <option value="popular">En Popüler</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="flex-1">
              <div className="mb-6 flex items-center justify-between">
                <p className="text-gray-600">
                  <strong className="text-gray-900">{filteredProducts.length}</strong> ürün bulundu
                </p>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <i className="ri-equalizer-line mr-2"></i>
                  Filtreler
                </button>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Ürünler yükleniyor...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center">
                  <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                    <i className="ri-search-line text-5xl text-gray-400"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Ürün Bulunamadı</h2>
                  <p className="text-gray-600 mb-6">Arama kriterlerinizi değiştirerek tekrar deneyin</p>
                  <button
                    onClick={resetFilters}
                    className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-700 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Filtreleri Sıfırla
                  </button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105 group cursor-pointer"
                      onClick={() => window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE(`/urun/${product.id}`)}
                    >
                      <div className="relative aspect-square w-full overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.title}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute top-3 right-3 px-3 py-1 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-sm font-semibold rounded-full">
                          {categories.find(c => c.name === product.category)?.label || product.category}
                        </div>
                        {product.stock < 50 && (
                          <div className="absolute top-3 left-3 px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-full">
                            Son {product.stock} adet!
                          </div>
                        )}
                      </div>

                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                          {product.title}
                        </h3>

                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <i
                                key={i}
                                className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-sm ${
                                  i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                              ></i>
                            ))}
                          </div>
                          <span className="text-xs text-gray-500">({product.reviews})</span>
                        </div>

                        <div className="flex items-center justify-between mb-3">
                          <p className="text-2xl font-bold text-pink-600">${product.price.toFixed(2)}</p>
                          <span className="text-sm text-green-600 font-medium">Stokta</span>
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE(`/urun/${product.id}`);
                          }}
                          className="w-full px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl font-semibold hover:from-pink-600 hover:to-purple-700 transition-all whitespace-nowrap cursor-pointer"
                        >
                          İncele
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SearchPage;